/**
 * Which OPTIONS a confirmed swap has just ruled out (B21, D60).
 *
 * The two halves of a plate are authored in different places and the till asks
 * for them in sequence: the swap dialog first, the option picker after it. The
 * server holds both at once and refuses two pairings — and both refusals are
 * right:
 *
 * - `DUPLICATE_REMOVAL` — the option and the swap take the SAME ingredient off.
 *   "Penne (replaces fettuccine)" beside an open "fettuccine → rigatoni"
 *   charges for both changes while the fold deletes fettuccine once, so one
 *   dish depletes two portions of pasta and the chit tells the cook to make
 *   two.
 * - `CHAINED_SWAP` — the option removes what the swap ADDS. The plate is billed
 *   for a change nothing cooks.
 *
 * Until this, the picker knew neither, so the collision was discovered by the
 * SERVER: the whole append was refused after the waiter had told the guest yes.
 * Nothing was corrupted and the refusal is translated — but the recovery
 * happened at the table rather than on the screen.
 *
 * This is a CONVENIENCE, exactly as `minSelect`/`maxSelect` in the picker are.
 * The server still refuses the pair; what this buys is that the floor is never
 * offered it.
 */

/** One authored removal: this option stands in for that recipe line. */
export type OptionRemoval = {
  addonId: string;
  replacesIngredientId: string;
};

/** One open swap as the till holds it while the picker is up. */
export type PendingSubstitution = {
  removesIngredientId: string;
  addsIngredientId: string;
};

/**
 * The option ids that cannot be picked while these swaps are pending.
 *
 * Both sides of every swap, because both refusals exist: an option removing the
 * swap's REMOVED ingredient is the duplicate, one removing its ADDED ingredient
 * is the chain. The picker greys out the result and says why, rather than
 * distinguishing the two on screen — the fix is the same either way ("take the
 * ingredient off in one place, not two").
 *
 * Empty in the ordinary case: an empty or absent removal list, or no pending
 * swap at all, blocks nothing.
 */
export function blockedAddonIds(
  optionRemovals: readonly OptionRemoval[] | undefined,
  substitutions: readonly PendingSubstitution[] | null | undefined,
): Set<string> {
  if (!optionRemovals?.length || !substitutions?.length) {
    return new Set();
  }
  const touched = new Set<string>();
  for (const substitution of substitutions) {
    touched.add(substitution.removesIngredientId);
    touched.add(substitution.addsIngredientId);
  }
  const blocked = new Set<string>();
  for (const removal of optionRemovals) {
    if (touched.has(removal.replacesIngredientId)) {
      blocked.add(removal.addonId);
    }
  }
  return blocked;
}
